import React from 'react';
import PropTypes from 'prop-types';
import { useStaticQuery, graphql } from 'gatsby';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import classNames from 'classnames/bind';
import Heading from 'components/shared/heading';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import Panel from './panel';

import shape1 from './svg/shape-1.svg';
import shape2 from './svg/shape-2.svg';

import styles from './home-dashboard.module.scss';

const cx = classNames.bind(styles);

const HomeDashboard = ({ title, description, items }) => {
  const { dashboard } = useStaticQuery(graphql`
    query {
      dashboard: file(relativePath: { eq: "home/cloud-dashboard/dashboard.jpg" }) {
        childImageSharp {
          gatsbyImageData(width: 980)
        }
      }
    }
  `);

  const [sectionRef, inView] = useInView({ threshold: 0.5, triggerOnce: true });

  const cardsAnimationVariants = {
    initial: {
      opacity: 0,
      y: 108,
    },
    appear: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: [0.21, 0.49, 0.33, 1] },
    },
  };

  const cardsContainerAnimationVariants = {
    initial: {},
    appear: {
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.4,
      },
    },
  };
  return (
    <section ref={sectionRef} className={cx('wrapper')}>
      <div className="container">
        <Heading className={cx('title')} tag="h2" size="xl" color="tertiary">
          {title}
        </Heading>
        <Heading
          className={cx('description')}
          tag="p"
          size="lg"
          color="tertiary"
          role="doc-subtitle"
        >
          {description}
        </Heading>

        <motion.div
          variants={cardsContainerAnimationVariants}
          className={cx('dashboard-wrapper')}
          animate={inView ? 'appear' : 'initial'}
        >
          {items.map(({ description: panelDescription }, index) => (
            <motion.div
              className={cx(`panel-${index + 1}`)}
              variants={cardsAnimationVariants}
              key={index}
            >
              <Panel>{panelDescription}</Panel>
            </motion.div>
          ))}

          <GatsbyImage className={cx('dashboard')} image={getImage(dashboard)} alt="" />

          <img className={cx('shape-1')} src={shape1} alt="" />
          <img className={cx('shape-2')} src={shape2} alt="" />
        </motion.div>
      </div>
    </section>
  );
};

HomeDashboard.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      description: PropTypes.string.isRequired,
    })
  ).isRequired,
};

export default HomeDashboard;
